import { useState } from "react"
import { X } from "@phosphor-icons/react"

import { useCompany, hasHakiDataAccess } from "~/hooks/useCompany"
import { Button, IconButton } from "~/components/reusable/Button"
import HakiFace from "~/components/reusable/HakiFace"
import HakiGlow from "~/components/reusable/HakiGlow"
import {
  Popover,
  PopoverContent,
  PopoverSection,
  PopoverTrigger,
} from "~/components/reusable/Popover"
import { Tooltip } from "~/components/reusable/Tooltip"
import { HakiDataAccessGateContent } from "~/components/reusable/HakiDataAccessGateContent"
import HakiPromptPopover from "./HakiPromptPopover"
import {
  buildExplainForecastPrompt,
  EXPLAIN_FORECAST_DISABLED_MESSAGE,
} from "~/server/api/routers/haki/explain-forecast-prompt"
import { api } from "~/utils/api"

interface ExplainForecastPopoverV2Props {
  variantId: string
  disabled?: boolean
}

function explainDisabledReason(disabled: boolean, superseded: boolean) {
  if (superseded) return EXPLAIN_FORECAST_DISABLED_MESSAGE
  if (disabled) return "Please select a single variant"
  return ""
}

function NoDataAccessPopover({ disabledReason }: { disabledReason: string }) {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip title={disabledReason}>
        <PopoverTrigger asChild>
          <div
            className="rounded-[8px] p-px inline-flex"
            style={{ backgroundImage: "var(--haki-brand-gradient)" }}
          >
            <Button
              size="sm"
              variant="neutral"
              disabled={!!disabledReason}
              className="rounded-[7px] shadow-none bg-base"
            >
              <HakiFace size={16} />
              Explain this demand
            </Button>
          </div>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        sideOffset={8}
        side="bottom"
        align="end"
        collisionPadding={16}
        className="w-[340px] max-w-[var(--radix-popover-content-available-width)] paragraph-base p-0 bg-transparent shadow-none border-0"
      >
        <HakiGlow>
          <div className="bg-base rounded-[16px] shadow-flyout flex flex-col divide-y divide-border-base overflow-hidden">
            <PopoverSection className="rounded-none p-3 flex flex-row items-center gap-2 shrink-0">
              <HakiFace size={16} />
              <div className="label-base flex-1">Explain this demand</div>
              <IconButton size="sm" variant="transparent" onClick={() => setOpen(false)}>
                <X />
              </IconButton>
            </PopoverSection>
            <PopoverSection className="rounded-none p-4 flex flex-col items-center gap-3 text-center">
              <HakiDataAccessGateContent />
            </PopoverSection>
          </div>
        </HakiGlow>
      </PopoverContent>
    </Popover>
  )
}

export default function ExplainForecastPopoverV2({
  variantId,
  disabled = false,
}: ExplainForecastPopoverV2Props) {
  const { data: company } = useCompany()
  const { data: superseded = false, isPending } = api.demand2.hasActivePublish.useQuery()
  const disabledReason = explainDisabledReason(disabled, superseded)

  if (!hasHakiDataAccess(company)) return <NoDataAccessPopover disabledReason={disabledReason} />

  return (
    <HakiPromptPopover
      triggerText="Explain this demand"
      prompt={buildExplainForecastPrompt(variantId)}
      disabled={isPending || !!disabledReason}
      disabledReason={disabledReason}
      trackingName="Forecast Explanation"
      source="explain-forecast"
    />
  )
}
